// Global
import React from 'react';

// Local
import { IngredientGroup, RecipeResponse } from 'lib/services/models/content-response';
import CtaLink from 'components/helpers/Atoms/Cta/CtaLink';
import { OneTrustFilter } from 'components/helpers/OneTrust';

import styles from './RecipeIngredients.module.scss';
import PearShopCTA from 'components/helpers/PearWidget/PearShopCTA';
import useDictionary from 'lib/hooks/useDictionary';
import { Field } from '@sitecore-content-sdk/nextjs';
import { useSiteSettings } from 'lib/hooks/useSiteSettings';

export interface RecipeIngredientsProps {
  recipe?: RecipeResponse;
}

const EMPTY_VALUE = '---';

function sortBySortOrder<T extends { SortOrder?: number }>(items: T[] = []): T[] {
  return [...items].sort(
    (a, b) => (a.SortOrder ?? Number.MAX_SAFE_INTEGER) - (b.SortOrder ?? Number.MAX_SAFE_INTEGER)
  );
}

function getDisplayValue(value?: string | null): string {
  return value?.trim() || EMPTY_VALUE;
}

function getIngredientKey(
  ingredient: IngredientGroup['Ingredients'][number],
  ingredientIndex: number
): string {
  return ingredient.ContentId || `${ingredient.Description}-${ingredientIndex}`;
}

function renderIngredientName(
  ingredientDescription: string,
  productName: string,
  productUrl: string | null
): React.ReactNode {
  if (!productUrl) {
    return ingredientDescription;
  }

  const safeDescription = ingredientDescription.trim();
  const safeProductName = productName.trim();

  if (!safeDescription) {
    return <CtaLink url={productUrl} text={EMPTY_VALUE} ctaStyle="none" ctaVariant="brand" />;
  }

  if (!safeProductName) {
    return <CtaLink url={productUrl} text={safeDescription} ctaStyle="none" ctaVariant="brand" />;
  }

  const lowerDescription = safeDescription.toLowerCase();
  const lowerProductName = safeProductName.toLowerCase();
  const productNameStart = lowerDescription.indexOf(lowerProductName);

  if (productNameStart === -1) {
    return <CtaLink url={productUrl} text={safeDescription} ctaStyle="none" ctaVariant="brand" />;
  }

  const productNameEnd = productNameStart + safeProductName.length;
  const prefix = safeDescription.slice(0, productNameStart);
  const matchedProductName = safeDescription.slice(productNameStart, productNameEnd);
  const suffix = safeDescription.slice(productNameEnd);

  return (
    <>
      {prefix}
      <CtaLink url={productUrl} text={matchedProductName} ctaStyle="none" ctaVariant="brand" />
      {suffix}
    </>
  );
}

export function RecipeIngredients(props: RecipeIngredientsProps) {
  const siteSettings = useSiteSettings();

  const { recipe } = props;

  const { getDictionaryValue } = useDictionary();

  const tableLayoutEnabled =
    (
      siteSettings?.recipeDetailSiteSettings?.fields?.enableTableDisplay as
        | Field<boolean>
        | undefined
    )?.value === true;
  const ingredientNameColumnHeading = getDictionaryValue(
    'Components.Recipe.RecipeDetailIngredients.IngredientColumnHeading',
    'Ingredient'
  );
  console.log('ingredientNameColumnHeading', ingredientNameColumnHeading);
  const ingredientWeightColumnHeading = getDictionaryValue(
    'Components.Recipe.RecipeDetailIngredients.WeightColumnHeading',
    'Weight'
  );
  const ingredientMeasureColumnHeading = getDictionaryValue(
    'Components.Recipe.RecipeDetailIngredients.MeasureColumnHeading',
    'Measure'
  );

  const hidePearShopButton = (
    siteSettings?.recipeDetailSiteSettings?.fields?.hideShopping as Field<boolean>
  )?.value;

  if (!recipe) {
    return <></>;
  }

  const sortedIngredientGroups = sortBySortOrder(recipe.IngredientGroups || []);

  return (
    <div className={styles.ingredients}>
      <div className={styles.ingredientsHeader}>
        <h2 className={styles.heading}>
          {getDictionaryValue('Components.Recipe.RecipeDetailIngredients.Headline', 'Ingredients')}
        </h2>
        {!hidePearShopButton && (
          <OneTrustFilter
            optanonGroups={['2', '3', '4']}
            enablePlaceholder={true}
            placeholderFeatureType="buynow"
            filteredComponent={
              <PearShopCTA
                id="pear-recipe-target"
                className="printHide"
                hideCondition={hidePearShopButton}
                text={getDictionaryValue(
                  'Components.Recipe.RecipeDetailIngredients.ShopCTAText',
                  'Shop recipe'
                )}
                ctaSize="small"
                ctaStyle="outline"
                ctaVariant="brand"
              />
            }
          />
        )}
      </div>

      {sortedIngredientGroups.map((ingredientGroup, ingredientGroupIndex) => (
        <div
          key={ingredientGroup.GroupName || `ingredient-group-${ingredientGroupIndex}`}
          className={styles.ingredientGroup}
        >
          {ingredientGroup.GroupName && (
            <h3 className={styles.groupTitle}>{ingredientGroup.GroupName}</h3>
          )}
          {tableLayoutEnabled ? (
            <IngredientListTable
              ingredientGroup={ingredientGroup}
              ingredientGroupIndex={ingredientGroupIndex}
              ingredientNameColumnHeading={ingredientNameColumnHeading}
              ingredientWeightColumnHeading={ingredientWeightColumnHeading}
              ingredientMeasureColumnHeading={ingredientMeasureColumnHeading}
            />
          ) : (
            <IngredientList ingredientGroup={ingredientGroup} />
          )}
        </div>
      ))}
    </div>
  );
}

function IngredientList({ ingredientGroup }: { ingredientGroup: IngredientGroup }) {
  const sortedIngredients = sortBySortOrder(ingredientGroup.Ingredients || []);

  return (
    <ul className={styles.ingredientList}>
      {sortedIngredients.map((ingredient, ingredientIndex) => {
        const description = [ingredient.Quantity, ingredient.Description].filter(Boolean).join(' ');

        if (!description) {
          return <></>;
        }

        return (
          <li key={getIngredientKey(ingredient, ingredientIndex)} className={styles.ingredientItem}>
            {ingredient?.ProductUrl ? (
              <CtaLink
                url={ingredient.ProductUrl}
                text={description}
                ctaStyle="none"
                ctaVariant="brand"
              />
            ) : (
              description
            )}
          </li>
        );
      })}
    </ul>
  );
}

function IngredientListTable({
  ingredientGroup,
  ingredientGroupIndex,
  ingredientNameColumnHeading,
  ingredientWeightColumnHeading,
  ingredientMeasureColumnHeading,
}: {
  ingredientGroup: IngredientGroup;
  ingredientGroupIndex: number;
  ingredientNameColumnHeading: string;
  ingredientWeightColumnHeading: string;
  ingredientMeasureColumnHeading: string;
}) {
  const { getDictionaryValue } = useDictionary();
  const sortedIngredients = sortBySortOrder(ingredientGroup.Ingredients || []);

  const tableAriaLabel = ingredientGroup.GroupName
    ? `${ingredientGroup.GroupName} ingredients`
    : `Ingredient group ${ingredientGroupIndex + 1}`;

  const hiddenA11yText = getDictionaryValue(
    'Common.NutritionFacts.Shared.HiddenA11yText.ValueNotAvailable',
    'Value not available'
  );

  const renderValueWithA11y = (displayValue: string): React.ReactNode => {
    if (displayValue === EMPTY_VALUE) {
      return (
        <>
          {displayValue}
          <span className="screenReaderText">{hiddenA11yText}</span>
        </>
      );
    }
    return displayValue;
  };

  return (
    <div className={styles.ingredientTableWrapper}>
      <table className={styles.ingredientTable} aria-label={tableAriaLabel}>
        <thead>
          <tr>
            <th scope="col">{ingredientNameColumnHeading}</th>
            <th scope="col">{ingredientWeightColumnHeading}</th>
            <th scope="col">{ingredientMeasureColumnHeading}</th>
          </tr>
        </thead>
        <tbody>
          {sortedIngredients.map((ingredient, ingredientIndex) => {
            const ingredientDescription = getDisplayValue(ingredient.Description);
            const ingredientWeight = getDisplayValue(ingredient.Weight?.Text);
            const ingredientMeasure = getDisplayValue(ingredient.Measure?.Text);

            return (
              <tr key={getIngredientKey(ingredient, ingredientIndex)}>
                <th scope="row" data-label={ingredientNameColumnHeading}>
                  {renderIngredientName(
                    ingredientDescription,
                    ingredient.IngredientBase?.Name || '',
                    ingredient.ProductUrl
                  )}
                </th>
                <td data-label={ingredientWeightColumnHeading}>
                  {renderValueWithA11y(ingredientWeight)}
                </td>
                <td data-label={ingredientMeasureColumnHeading}>
                  {renderValueWithA11y(ingredientMeasure)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
